import React, { useState, useMemo } from "react";
import { Search, Plus, MoreVertical, Pencil, Trash2 } from "../../utils/icons";
import Card from "../../components/ui/Card";
import Avatar from "../../components/ui/Avatar";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import Modal from "../../components/ui/Modal";
import { users as initialUsers } from "../../utils/dummyData";

const statusBadgeColor = {
  Active: "green",
  Inactive: "gray",
  Pending: "yellow",
};
const planBadgeColor = {
  Enterprise: "violet",
  Pro: "primary",
  Starter: "cyan",
};

const Users = () => {
  const [users, setUsers] = useState(initialUsers);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "Viewer",
    status: "Active",
    plan: "Starter",
  });
  const [menuOpen, setMenuOpen] = useState(null);

  const filtered = useMemo(
    () =>
      users.filter((u) => {
        const matchSearch =
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase());
        const matchFilter = filter === "All" || u.status === filter;
        return matchSearch && matchFilter;
      }),
    [users, search, filter],
  );

  const openAdd = () => {
    setEditUser(null);
    setForm({
      name: "",
      email: "",
      role: "Viewer",
      status: "Active",
      plan: "Starter",
    });
    setModalOpen(true);
  };
  const openEdit = (user) => {
    setEditUser(user);
    setForm({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status,
      plan: user.plan,
    });
    setModalOpen(true);
    setMenuOpen(null);
  };

  const handleSave = () => {
    if (!form.name.trim() || !form.email.trim()) return;
    if (editUser) {
      setUsers((prev) =>
        prev.map((u) => (u.id === editUser.id ? { ...u, ...form } : u)),
      );
    } else {
      setUsers((prev) => [
        ...prev,
        {
          id: Date.now(),
          ...form,
          joined: new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          avatar: form.name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2),
        },
      ]);
    }
    setModalOpen(false);
  };

  const handleDelete = (id) => {
    setUsers((prev) => prev.filter((u) => u.id !== id));
    setDeleteModal(null);
    setMenuOpen(null);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900 dark:text-white">
            Users
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {users.length} total members
          </p>
        </div>
        <Button onClick={openAdd} icon={<Plus size={16} />}>
          Add User
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="input-field !pl-9 text-sm"
          />
        </div>
        <div className="flex gap-2">
          {["All", "Active", "Inactive", "Pending"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${filter === f ? "bg-primary-600 text-white" : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary-400"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700">
                {["User", "Role", "Status", "Plan", "Joined", ""].map((h) => (
                  <th
                    key={h}
                    className="text-left px-5 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">
                    No users found
                  </td>
                </tr>
              ) : (
                filtered.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={user.name} size="md" />
                        <div>
                          <p className="font-medium text-sm text-gray-900 dark:text-white">
                            {user.name}
                          </p>
                          <p className="text-xs text-gray-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {user.role}
                    </td>
                    <td className="px-5 py-4">
                      <Badge color={statusBadgeColor[user.status]}>
                        {user.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-4">
                      <Badge color={planBadgeColor[user.plan]}>
                        {user.plan}
                      </Badge>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {user.joined}
                    </td>
                    <td className="px-5 py-4 relative">
                      <button
                        onClick={() =>
                          setMenuOpen(menuOpen === user.id ? null : user.id)
                        }
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
                      >
                        <MoreVertical size={16} />
                      </button>
                      {menuOpen === user.id && (
                        <div className="absolute right-8 top-6 z-10 w-36 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl overflow-hidden animate-fade-in">
                          <button
                            onClick={() => openEdit(user)}
                            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            <Pencil size={14} /> Edit
                          </button>
                          <button
                            onClick={() => {
                              setDeleteModal(user);
                              setMenuOpen(null);
                            }}
                            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-sm text-gray-500">
          <span>
            Showing {filtered.length} of {users.length} users
          </span>
          <div className="flex gap-1">
            {["1", "2", "3"].map((p, i) => (
              <button
                key={p}
                className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${i === 0 ? "bg-primary-600 text-white" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editUser ? "Edit User" : "Add New User"}
      >
        <div className="space-y-4">
          {[
            {
              label: "Full Name",
              key: "name",
              type: "text",
              placeholder: "Jane Smith",
            },
            {
              label: "Email Address",
              key: "email",
              type: "email",
              placeholder: "jane@company.com",
            },
          ].map(({ label, key, type, placeholder }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {label}
              </label>
              <input
                type={type}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="input-field"
                placeholder={placeholder}
              />
            </div>
          ))}
          <div className="grid grid-cols-3 gap-3">
            {[
              {
                label: "Role",
                key: "role",
                options: ["Admin", "Editor", "Viewer"],
              },
              {
                label: "Status",
                key: "status",
                options: ["Active", "Inactive", "Pending"],
              },
              {
                label: "Plan",
                key: "plan",
                options: ["Starter", "Pro", "Enterprise"],
              },
            ].map(({ label, key, options }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {label}
                </label>
                <select
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="input-field"
                >
                  {options.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              variant="ghost"
              className="flex-1"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button className="flex-1" onClick={handleSave}>
              {editUser ? "Save Changes" : "Add User"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Delete User"
        size="sm"
      >
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Are you sure you want to delete{" "}
          <strong className="text-gray-900 dark:text-white">
            {deleteModal?.name}
          </strong>
          ? This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <Button
            variant="ghost"
            className="flex-1"
            onClick={() => setDeleteModal(null)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className="flex-1"
            onClick={() => handleDelete(deleteModal.id)}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Users;
